import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Modal, ModalHeader, ModalTitle, ModalContent, ModalFooter } from '../ui/Modal';
import { Button } from '../ui/Button';
import TextInput from '../ui/TextInput';
import { useTheme, type ThemeColors } from '../../contexts/ThemeContext';
import { useNotification } from '../../contexts/NotificationContext';
import { cn } from '../../lib/utils';
import { XIcon, SearchIcon, TrashIcon, StarIcon, GripVerticalIcon, PencilIcon, CheckIcon } from '../ui/Icons';
import {
  getBookmarks,
  addToBookmarks,
  addMultipleToBookmarks,
  removeFromBookmarks,
  reorderBookmarks,
  getAllMenuItems,
  BookmarkItem,
} from '../../utils/bookmarks';
import { getSidebarItems } from './config/SidebarItems';
import { useAuth } from '../../contexts/AuthContext';
import { useAccountVesselScope } from '../../hooks/useAccountVesselScope';
import { useModuleEntitlements } from '../../modules/admin/module-management';

interface BookmarksManagerProps {
  open: boolean;
  onClose: () => void;
}

type AvailableItem = { title: string; to: string; icon?: React.ReactNode; fullPath: string };

const BookmarkRow = React.memo(function BookmarkRow(props: {
  fav: BookmarkItem;
  index: number;
  isEditing: boolean;
  editingTitle: string;
  onClose: () => void;
  onEditingTitleChange: (value: string) => void;
  onStartEdit: (fav: BookmarkItem) => void;
  onSaveEdit: (route: string) => void;
  onCancelEdit: () => void;
  onRemove: (route: string) => void;
  onDragStart: (e: React.DragEvent, index: number) => void;
  onDragEnd: (e: React.DragEvent) => void;
  onDragOver: (e: React.DragEvent, index: number) => void;
  onDragLeave: () => void;
  onDrop: (e: React.DragEvent, index: number) => void;
  draggedIndex: number | null;
  dragOverIndex: number | null;
  colors: ThemeColors;
}) {
  const {
    fav,
    index,
    isEditing,
    editingTitle,
    onClose,
    onEditingTitleChange,
    onStartEdit,
    onSaveEdit,
    onCancelEdit,
    onRemove,
    onDragStart,
    onDragEnd,
    onDragOver,
    onDragLeave,
    onDrop,
    draggedIndex,
    dragOverIndex,
    colors,
  } = props;
  return (
    <div
      draggable
      onDragStart={(e) => onDragStart(e, index)}
      onDragEnd={onDragEnd}
      onDragOver={(e) => onDragOver(e, index)}
      onDragLeave={onDragLeave}
      onDrop={(e) => onDrop(e, index)}
      className={cn(
        'flex items-center justify-between p-3 transition-all cursor-move',
        colors.surface,
        draggedIndex === index && 'opacity-50',
        dragOverIndex === index && 'border-t-2 border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20',
        dragOverIndex !== index && 'hover:bg-gray-50 dark:hover:bg-gray-800'
      )}
    >
      <div className={cn('p-1 mr-2 cursor-grab active:cursor-grabbing', colors.textMuted)}>
        <GripVerticalIcon className="w-4 h-4" />
      </div>
      <div className="flex items-center gap-3 flex-1 min-w-0">
        <StarIcon className="w-4 h-4 text-yellow-500 flex-shrink-0" filled />
        <div className="flex-1 min-w-0">
          {isEditing ? (
            <div className="flex items-center gap-2">
              <TextInput
                type="text"
                value={editingTitle}
                onChange={(e: { target: { value: string } }) => onEditingTitleChange(e.target.value)}
                className="h-8 text-sm"
                autoFocus
                onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    onSaveEdit(fav.to);
                  } else if (e.key === 'Escape') {
                    e.preventDefault();
                    onCancelEdit();
                  }
                }}
              />
              <button
                onClick={() => onSaveEdit(fav.to)}
                className={cn('p-1 rounded-lg hover:bg-green-50 dark:hover:bg-green-900/20 transition-colors', colors.textSecondary)}
                title="Save name"
              >
                <CheckIcon className="w-4 h-4 text-green-600" />
              </button>
              <button
                onClick={onCancelEdit}
                className={cn('p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors', colors.textSecondary)}
                title="Cancel"
              >
                <XIcon className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <>
              <Link
                to={fav.to}
                className="flex items-center gap-3 flex-1 min-w-0"
                onClick={onClose}
                draggable={false}
              >
                <div className="flex flex-col min-w-0">
                  <div className={cn('text-sm font-medium truncate', colors.text)}>{fav.title}</div>
                  <div className={cn('text-xs truncate', colors.textSecondary)}>
                    {fav.fullPath || fav.to}
                  </div>
                </div>
              </Link>
            </>
          )}
        </div>
      </div>
      {!isEditing && (
        <button
          onClick={() => onStartEdit(fav)}
          className={cn('p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors ml-2', colors.textSecondary)}
          title="Rename"
          draggable={false}
        >
          <PencilIcon className="w-4 h-4" />
        </button>
      )}
      <button
        onClick={() => onRemove(fav.to)}
        className={cn('p-1.5 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors ml-1', colors.textSecondary)}
        title="Remove from bookmarks"
        draggable={false}
      >
        <TrashIcon className="w-4 h-4 text-red-500" />
      </button>
    </div>
  );
});

const AvailableItemRow = React.memo(function AvailableItemRow(props: {
  item: AvailableItem;
  isBookmarked: boolean;
  isSelected: boolean;
  onToggleSelect: (item: { to: string }) => void;
  onAddBookmark: (item: { title: string; to: string; fullPath?: string }) => void;
  onRemoveBookmark: (route: string) => void;
  colors: ThemeColors;
}) {
  const { item, isBookmarked, isSelected, onToggleSelect, onAddBookmark, onRemoveBookmark, colors } = props;
  return (
    <div
      className={cn(
        'flex items-center justify-between p-3 transition-colors',
        isBookmarked
          ? 'bg-yellow-50 dark:bg-yellow-900/10 border-l-4 border-yellow-500'
          : isSelected
          ? 'bg-blue-50 dark:bg-blue-900/10'
          : 'hover:bg-gray-50 dark:hover:bg-gray-800',
        colors.surface
      )}
    >
      <div className="flex items-center gap-3 flex-1 min-w-0">
        {!isBookmarked && (
          <input
            type="checkbox"
            checked={isSelected}
            onChange={() => onToggleSelect(item)}
            className="w-4 h-4 text-indigo-600 rounded focus:ring-indigo-500"
          />
        )}
        {isBookmarked && (
          <span className={`px-2 py-0.5 text-xs font-medium bg-yellow-500 ${colors.primaryText} rounded`}>
            In Bookmarks
          </span>
        )}
        <div className="flex items-center gap-2 flex-1 min-w-0">
          {item.icon && <span className="flex-shrink-0">{item.icon}</span>}
          <div className="flex-1 min-w-0">
            <div className={cn('text-sm font-medium truncate flex items-center gap-1', colors.text)}>
              {item.title}
              {isBookmarked && <StarIcon className="w-3.5 h-3.5 text-yellow-500" filled />}
            </div>
            <div className={cn('text-xs truncate', colors.textSecondary)}>
              {item.fullPath || item.to}
            </div>
          </div>
        </div>
      </div>
      <button
        onClick={() => {
          if (isBookmarked) {
            onRemoveBookmark(item.to);
          } else {
            onAddBookmark(item);
          }
        }}
        className={cn(
          'p-1.5 rounded-lg transition-colors ml-2',
          isBookmarked
            ? 'text-yellow-500 hover:bg-yellow-100 dark:hover:bg-yellow-900/20'
            : `${colors.textMuted} hover:text-yellow-500 hover:${colors.surfaceHover}`
        )}
        title={isBookmarked ? 'Remove from bookmarks' : 'Add to bookmarks'}
      >
        <StarIcon className="w-4 h-4" filled={isBookmarked} />
      </button>
    </div>
  );
});

const BookmarksManager: React.FC<BookmarksManagerProps> = ({ open, onClose }) => {
  const { colors } = useTheme();
  const { showSuccess, showError, showInfo } = useNotification();
  const { user, can, permissionsLoaded } = useAuth();
  const vesselAccountId = useAccountVesselScope();
  const { entitlements } = useModuleEntitlements();
  const canView = React.useMemo(
    () => (permissionsLoaded || user?.role === 'ADMIN' ? (moduleKey: string) => can(moduleKey, 'view') : undefined),
    [can, permissionsLoaded, user?.role]
  );
  const [bookmarks, setBookmarks] = useState<BookmarkItem[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [availableItems, setAvailableItems] = useState<Array<{ title: string; to: string; icon?: React.ReactNode; fullPath: string }>>([]);
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const [editingRoute, setEditingRoute] = useState<string | null>(null);
  const [editingTitle, setEditingTitle] = useState<string>('');
  const editingTitleRef = useRef(editingTitle);
  editingTitleRef.current = editingTitle;

  useEffect(() => {
    if (open) {
      loadBookmarks();
      loadAvailableItems();
      setSelectedItems(new Set());
    }
  }, [open]);


  const loadBookmarks = async () => {
    try {
      setLoading(true);

      // Routes this user may see in the sidebar (per permissions)
      let permittedSidebarRoutes: Set<string> | undefined;
      try {
        const menuItems = getSidebarItems(canView, vesselAccountId, entitlements);
        const allItems = getAllMenuItems(menuItems);
        permittedSidebarRoutes = new Set(allItems.map((item) => item.to));
      } catch (innerError) {
        console.warn('Failed to compute permitted sidebar routes for bookmarks, falling back to all bookmarks:', innerError);
      }

      const favs = await getBookmarks();

      const filteredFavs =
        permittedSidebarRoutes != null ? favs.filter((fav) => permittedSidebarRoutes!.has(fav.to)) : favs;

      setBookmarks(filteredFavs);
    } catch (error: any) {
      console.error('Error loading bookmarks:', error);
      setBookmarks([]);
      if (!error.message || (!error.message.includes('expired') && !error.message.includes('Unauthorized'))) {
        showError('Failed to load bookmarks');
      }
    } finally {
      setLoading(false);
    }
  };

  const loadAvailableItems = () => {
    const menuItems = getSidebarItems(canView, vesselAccountId, entitlements);
    const allItems = getAllMenuItems(menuItems);
    setAvailableItems(allItems);
  };

  const handleAddBookmark = useCallback(
    async (item: { title: string; to: string; fullPath?: string }) => {
      try {
        const result = await addToBookmarks(item);
        if (result) {
          showSuccess('Added to bookmarks');
          await loadBookmarks();
          loadAvailableItems();
        } else {
          showInfo('Already in bookmarks');
        }
      } catch (error: unknown) {
        console.error('Error adding bookmark:', error);
        showError('Failed to add bookmark: ' + (error instanceof Error ? error.message : 'Unknown error'));
      }
    },
    [showSuccess, showInfo, showError]
  );

  const handleRemoveBookmark = useCallback(
    async (route: string) => {
      try {
        const result = await removeFromBookmarks(route);
        if (result) {
          showSuccess('Removed from bookmarks');
          await loadBookmarks();
        }
      } catch (error: unknown) {
        console.error('Error removing bookmark:', error);
        showError('Failed to remove bookmark');
      }
    },
    [showSuccess, showError]
  );

  const handleDragStart = useCallback((e: React.DragEvent, index: number) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    setTimeout(() => {
      (e.target as HTMLElement).style.opacity = '0.5';
    }, 0);
  }, []);

  const handleDragEnd = useCallback((e: React.DragEvent) => {
    (e.target as HTMLElement).style.opacity = '1';
    setDraggedIndex(null);
    setDragOverIndex(null);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverIndex((prev) => (prev !== index ? index : prev));
  }, []);

  const handleDragLeave = useCallback(() => setDragOverIndex(null), []);

  const BookmarksRef = useRef(bookmarks);
  BookmarksRef.current = bookmarks;
  const handleDrop = useCallback(
    async (e: React.DragEvent, dropIndex: number) => {
      e.preventDefault();
      const di = draggedIndex;
      if (di === null || di === dropIndex) {
        setDraggedIndex(null);
        setDragOverIndex(null);
        return;
      }
      const currentFavs = BookmarksRef.current;
      const newBookmarks = [...currentFavs];
      const [draggedItem] = newBookmarks.splice(di, 1);
      newBookmarks.splice(dropIndex, 0, draggedItem);
      setBookmarks(newBookmarks);
      setDraggedIndex(null);
      setDragOverIndex(null);
      try {
        const result = await reorderBookmarks(newBookmarks);
        if (result) {
          showSuccess('bookmarks reordered');
          window.dispatchEvent(new Event('bookmarksUpdated'));
        } else {
          await loadBookmarks();
          showError('Failed to save new order');
        }
      } catch (error) {
        console.error('Error reordering bookmarks:', error);
        await loadBookmarks();
        showError('Failed to reorder bookmarks');
      }
    },
    [draggedIndex, showSuccess, showError]
  );

  const handleStartEdit = useCallback((fav: BookmarkItem) => {
    setEditingRoute(fav.to);
    setEditingTitle(fav.title);
  }, []);

  const handleCancelEdit = useCallback(() => {
    setEditingRoute(null);
    setEditingTitle('');
  }, []);

  const handleSaveEdit = useCallback(
    async (route: string) => {
      const newTitle = editingTitleRef.current.trim();
      if (!newTitle) {
        showInfo('Name cannot be empty');
        return;
      }
      const updatedBookmarks = bookmarks.map((fav) =>
        fav.to === route ? { ...fav, title: newTitle } : fav
      );
      setBookmarks(updatedBookmarks);
      setEditingRoute(null);
      setEditingTitle('');
      try {
        const result = await reorderBookmarks(updatedBookmarks);
        if (!result) {
          showError('Failed to save bookmark name');
          await loadBookmarks();
        } else {
          showSuccess('Bookmark name updated');
        }
      } catch (error) {
        console.error('Error updating bookmark name:', error);
        showError('Failed to update bookmark name');
        await loadBookmarks();
      }
    },
    [bookmarks, showInfo, showSuccess, showError]
  );

  const handleToggleSelect = useCallback((item: { to: string }) => {
    setSelectedItems((prev) => {
      const next = new Set(prev);
      if (next.has(item.to)) next.delete(item.to);
      else next.add(item.to);
      return next;
    });
  }, []);

  const handleSelectAll = () => {
    const bookmarkRoutes = bookmarks.map(f => f.to);
    const notBookmarkItems = filteredAvailableItems.filter(item => !bookmarkRoutes.includes(item.to));
    
    if (notBookmarkItems.length === 0) {
      setSelectedItems(new Set());
      return;
    }

    const allSelected = notBookmarkItems.every(item => selectedItems.has(item.to));
    if (allSelected) {
      // Deselect all
      const newSelected = new Set(selectedItems);
      notBookmarkItems.forEach(item => newSelected.delete(item.to));
      setSelectedItems(newSelected);
    } else {
      // Select all
      const newSelected = new Set(selectedItems);
      notBookmarkItems.forEach(item => newSelected.add(item.to));
      setSelectedItems(newSelected);
    }
  };

  const handleAddSelectedBookmarks = async () => {
    if (selectedItems.size === 0) {
      showInfo('Please select at least one item');
      return;
    }

    try {
      setLoading(true);
      const bookmarkRoutes = bookmarks.map(f => f.to);
      
      // Filter from all availableItems, not just filteredAvailableItems
      // This ensures we can add items even if they're not currently visible due to search
      const itemsToAdd = availableItems.filter(
        item => selectedItems.has(item.to) && !bookmarkRoutes.includes(item.to)
      );

      console.log('Selected items:', Array.from(selectedItems));
      console.log('Items to add:', itemsToAdd);
      console.log('Bookmark routes:', bookmarkRoutes);

      if (itemsToAdd.length === 0) {
        showInfo('Selected items are already in bookmarks');
        setSelectedItems(new Set());
        setLoading(false);
        return;
      }

      // Map to the format expected by addMultipleToBookmarks
      const itemsToAddFormatted = itemsToAdd.map(item => ({
        title: item.title,
        to: item.to,
        fullPath: item.fullPath,
      }));

      console.log('Items formatted for API:', itemsToAddFormatted);

      const result = await addMultipleToBookmarks(itemsToAddFormatted);
      
      console.log('Add result:', result);
      
      if (result.success) {
        showSuccess(result.message);
        await loadBookmarks();
        loadAvailableItems(); // Refresh available items to update the UI
        setSelectedItems(new Set());
        // Trigger bookmarks update event for sidebar
        window.dispatchEvent(new Event('bookmarksUpdated'));
      } else {
        showInfo(result.message || 'Failed to add selected items');
        await loadBookmarks();
        loadAvailableItems(); // Refresh available items to update the UI
        setSelectedItems(new Set());
      }
    } catch (error: any) {
      console.error('Error adding selected bookmarks:', error);
      showError('Failed to add selected items: ' + (error.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  const filteredAvailableItems = useMemo(() => {
    const searchLower = searchTerm.toLowerCase().trim();
    if (!searchLower) return availableItems;
    return availableItems.filter(
      (item) =>
        item.title.toLowerCase().includes(searchLower) ||
        item.fullPath?.toLowerCase().includes(searchLower) ||
        item.to?.toLowerCase().includes(searchLower)
    );
  }, [availableItems, searchTerm]);

  const groupedAvailableItems = useMemo(() => {
    const groups: Record<string, typeof availableItems> = {};
    filteredAvailableItems.forEach((item) => {
      const groupLabel = item.fullPath?.split(' > ')[0] || 'Other';
      if (!groups[groupLabel]) {
        groups[groupLabel] = [];
      }
      groups[groupLabel].push(item);
    });
    return groups;
  }, [filteredAvailableItems]);

  const bookmarkRoutes = bookmarks.map(f => f.to);
  
  const isItemBookmarked = (item: { to: string }) => bookmarkRoutes.includes(item.to);

  return (
    <Modal isOpen={open} onClose={onClose} size="lg">
      <ModalHeader>
        <div className="flex items-center gap-2">
          <StarIcon className="w-5 h-5 text-yellow-500" filled />
          <ModalTitle>Manage Bookmarks</ModalTitle>
        </div>
        <button
          onClick={onClose}
          className={cn('p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors', colors.textSecondary)}
        >
          <XIcon className="w-5 h-5" />
        </button>
      </ModalHeader>
      <ModalContent>
        <div className="space-y-6">
          {/* Your bookmarks Section */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <h3 className={cn('text-sm font-medium', colors.text)}>
                Your Bookmarks ({bookmarks.length})
              </h3>
              {bookmarks.length > 1 && (
                <span className={cn('text-xs', colors.textMuted)}>
                  Drag to reorder
                </span>
              )}
            </div>
            {bookmarks.length > 0 ? (
              <div className={cn('border rounded-lg divide-y', colors.border)}>
                {bookmarks.map((fav, index) => (
                  <BookmarkRow
                    key={fav.to}
                    fav={fav}
                    index={index}
                    isEditing={editingRoute === fav.to}
                    editingTitle={editingTitle}
                    onClose={onClose}
                    onEditingTitleChange={setEditingTitle}
                    onStartEdit={handleStartEdit}
                    onSaveEdit={handleSaveEdit}
                    onCancelEdit={handleCancelEdit}
                    onRemove={handleRemoveBookmark}
                    onDragStart={handleDragStart}
                    onDragEnd={handleDragEnd}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    draggedIndex={draggedIndex}
                    dragOverIndex={dragOverIndex}
                    colors={colors}
                  />
                ))}
              </div>
            ) : (
              <div className={cn('p-6 text-center border border-dashed rounded-lg', colors.border, colors.surface)}>
                <p className={cn('text-sm', colors.textSecondary)}>
                  No bookmarks yet. Add some below!
                </p>
              </div>
            )}
          </div>

          {/* Available Items Section */}
          <div>
            <div className="relative mb-3">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <SearchIcon className={cn('w-4 h-4', colors.textMuted)} />
              </div>
              <TextInput
                type="text"
                placeholder="Search for forms to add..."
                value={searchTerm}
                onChange={(e: { target: { value: string } }) => setSearchTerm(e.target.value)}
                className="pl-10 w-full"
              />
            </div>

            <div className="flex items-center justify-between mb-2">
              <h3 className={cn('text-sm font-medium', colors.text)}>
                Available Forms ({filteredAvailableItems.length})
              </h3>
              <div className="flex items-center gap-2">
                {selectedItems.size > 0 && (
                  <span className={cn('text-xs px-2 py-1 rounded-full bg-blue-100 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300', colors.textSecondary)}>
                    {selectedItems.size} selected
                  </span>
                )}
                <Button variant="ghost" size="sm" onClick={handleSelectAll}>
                  {(() => {
                    const bookmarkRoutes = bookmarks.map(f => f.to);
                    const notBookmarkItems = filteredAvailableItems.filter(item => !bookmarkRoutes.includes(item.to));
                    const allSelected = notBookmarkItems.length > 0 && notBookmarkItems.every(item => selectedItems.has(item.to));
                    return allSelected ? 'Deselect All' : 'Select All';
                  })()}
                </Button>
                {selectedItems.size > 0 && (
                  <Button 
                    variant="default" 
                    size="sm" 
                    onClick={handleAddSelectedBookmarks}
                    disabled={loading}
                    loading={loading}
                  >
                    Add Selected ({selectedItems.size})
                  </Button>
                )}
              </div>
            </div>

            <div className={cn('border rounded-lg', colors.border)}>
              {filteredAvailableItems.length > 0 ? (
                <div className="divide-y">
                  {Object.entries(groupedAvailableItems).map(([groupLabel, items]) => (
                    <div key={groupLabel}>
                      <div className={cn('px-3 py-2 text-xs font-semibold uppercase tracking-wide', colors.surface, colors.textSecondary)}>
                        {groupLabel}
                      </div>
                      <div className="divide-y">
                        {items.map((item) => (
                          <AvailableItemRow
                            key={item.to}
                            item={item}
                            isBookmarked={bookmarkRoutes.includes(item.to)}
                            isSelected={selectedItems.has(item.to)}
                            onToggleSelect={handleToggleSelect}
                            onAddBookmark={handleAddBookmark}
                            onRemoveBookmark={handleRemoveBookmark}
                            colors={colors}
                          />
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className={cn('p-6 text-center', colors.textSecondary)}>
                  <p className="text-sm">
                    {searchTerm ? 'No results found' : 'No items available'}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </ModalContent>
      <ModalFooter>
        <Button onClick={onClose} variant="default">
          Done
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default BookmarksManager;

